<template>
    <section class="header-search-slice" :class="{'search-page': isSearchPage}">
        <div class="header-search-slice__title">{{ asText(slice.primary.title) }}</div>
        <SearchPanel
            v-if="!isSearchPage"
            :search-tab="searchLinkText"
            :search-field-category="slice.primary.categoryLabel"
            :search-field-location="slice.primary.locationLabel"
            :search-location-remote-country-label="slice.primary.remoteInCountryLabel"
            :search-location-remote-europe-label="slice.primary.remoteInEuropeLabel"
            :search-submit="searchLinkText"
            :search-submit-link="slice.primary.searchLink"
            :ai-search-tab="slice.primary.aiSearchLinkText"
            :ai-search-tab-link="slice.primary.aiSearchLink"
            :ai-entity-detection-enabled="$ff('one-entry-point-improvements-unlogged-homepage')"
            brief-origin="top_nav"
            page-source="top_nav_try_ai_search"
            origin="USER_PROMPT_NAVBAR_UNLOGGED"
        />
        <FreelanceSearch
            v-else
            :category-label="slice.primary.categoryLabel"
            :location-label="slice.primary.locationLabel"
            :search-link-text="searchLinkText"
            :search-link="slice.primary.searchLink"
            :submit-project-link-text="slice.primary.submitProjectLinkText"
            :submit-project-link="slice.primary.submitProjectLink"
            :ai-search-link-text="slice.primary.aiSearchLinkText"
            :ai-search-link="slice.primary.aiSearchLink"
            :between-ctas-text="slice.primary.betweenCTAsText"
            :missing-category-error-text="slice.primary.missingCategoryErrorText"
            :remote-in-country-label="slice.primary.remoteInCountryLabel"
            heap-prefix="navbar-unlogged-search-expand"
            brief-origin="top_nav_search_results_unlogged"
        />
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useSearchRouteDefinitions} from '@navbar/features/search';
    import FreelanceSearch from '@navbar-unlogged/components/FreelanceSearch.vue';
    import {asText} from '@prismicio/helpers';
    import {getSliceComponentProps} from '@prismicio/vue';
    import type {HeaderSearchSliceSlice} from '@prismicio-types';
    import SearchPanel from '@search/components/SearchPanel.vue';
    import {useTranslation} from '#imports';
    import {useABTest} from '@malt/feature-flags';

    const props = defineProps(getSliceComponentProps<HeaderSearchSliceSlice>());

    const {currentPage} = useSearchRouteDefinitions();
    const isSearchPage = computed(() => currentPage.value?.name === 'Search');

    const {t} = useTranslation();
    const {getVariation} = useABTest();

    const searchLinkText = computed(() =>
        getVariation('keyword-search-cta-label-change') === 'treatment'
            ? t('search.advanced.ai.entrypoint.mode.keyword.search.switch.button.browse')
            : props.slice.primary.searchLinkText,
    );
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@navbar-unlogged/scss/variables.scss' as vars;

    .header-search-slice {
        padding-top: var(--joy-core-spacing-8);

        @include mq.screen_sm(min) {
            padding: 48px 80px;
        }

        &__title {
            font-size: var(--joy-font-size-primary-800);
            font-weight: var(--joy-font-weight-normal);
            font-family: var(--joy-font-family-title);
            color: var(--joy-color-neutral-60);
            margin-bottom: var(--joy-core-spacing-8);

            @include mq.screen_sm(min) {
                font-size: var(--joy-font-size-secondary-600);
            }
        }

        :deep(.header-search-slice__title) {
            text-align: left;
            margin-bottom: var(--joy-core-spacing-8);

            @include vars.secondary-title;

            line-height: var(--joy-line-height-large);
        }

        :deep(.freelance-search) {
            margin-top: 24px;

            .search-card {
                box-shadow: none;
                padding: 0;

                .inputs {
                    .location-input,
                    .category-input,
                    .location,
                    .category {
                        max-width: none;
                    }
                }

                button[type='submit'] {
                    &:focus,
                    &:focus-visible {
                        border-color: var(--joy-color-secondary-30);
                    }

                    @include vars.button-primary-blue;

                    padding: 12px 40px;
                }
            }
        }
    }

    .search-page {
        padding: var(--joy-core-spacing-3) 80px;

        @include mq.screen_sm {
            padding: var(--joy-core-spacing-3) 0;
        }

        .header-search-slice__title {
            display: none;
        }

        :deep(section.freelance-search) {
            margin: 0;
        }
    }
</style>
